import React, { createContext, useContext, useState, useEffect } from 'react';
import NotificationService from '../services/NotificationService.tsx';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';

import { PushNotifications } from '@capacitor/push-notifications';
import { getPlatformType, isNotificationSupported } from '../utils/platformUtils';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [currentNotification, setCurrentNotification] = useState(null);
  const [currentToken, setCurrentToken] = useState(null);
  const { currentUser } = useAuth();
  const { organizationId, loading: orgLoading } = useOrganization();
  const notificationService = NotificationService.getInstance();
  const [initialized, setInitialized] = useState(false);
  const [platformType, setPlatformType] = useState(null);

  useEffect(() => {
    
    const platform = getPlatformType();
    setPlatformType(platform);
    
    // Check initial notification permission based on platform
    if (platform === 'ios' || platform === 'android') {
      PushNotifications.checkPermissions().then(({ receive }) => {
        setNotificationPermission(receive);
      }).catch(error => {
        // Silently handle permission check errors
      });
    } else if (platform === 'web' && window.Notification) {
      setNotificationPermission(Notification.permission);
    } else if (platform === 'ios-pwa') {
      setNotificationPermission('unsupported');
    }

    // Set token listener, but don't initialize yet
    notificationService.setOnTokenReceived((token) => {
      setCurrentToken(token);
    });

    // Set notification listener for state management
    notificationService.setOnNotificationReceived((notification) => {
      setCurrentNotification(notification);
    });
  }, [notificationService]);

  // Set current user and initialize notification service when user changes
  useEffect(() => {
    let isMounted = true;

    const initializeNotifications = async () => {
    if (currentUser?.uid && organizationId && !orgLoading && !initialized && isNotificationSupported()) {
      notificationService.setCurrentUser(currentUser.uid, currentUser.displayName || currentUser.email);
      notificationService.setCurrentOrganization(organizationId);
        try {
          await notificationService.initialize();
          if (isMounted) {
            setInitialized(true);
          }
        } catch (error) {
        // Silently handle initialization errors
        }
    } else if (!currentUser && initialized) {
        const serviceToken = notificationService.getCurrentToken();
        if (serviceToken) {
          notificationService.removeNotificationToken(serviceToken).catch(error => {
            // Silently handle token removal errors
          });
        }
        
        notificationService.removeAllTokensForUser().catch(error => {
          // Silently handle token removal errors
        });
        
        notificationService.clearCurrentToken();
        notificationService.cleanup(); // This will remove all listeners and reset state
        
        if (isMounted) {
          setInitialized(false);
          setCurrentToken(null);
        }
      }
    };

    initializeNotifications();

    return () => {
      isMounted = false;
    };
  }, [currentUser, organizationId, orgLoading, initialized, notificationService]);

  // Removed duplicate event listener - NotificationService handles all notification display
  // This prevents duplicate notifications while maintaining proper permission flow

  const value = {
    notificationPermission,
    currentNotification,
    currentToken,
    platformType,
    isNotificationSupported: isNotificationSupported(),
    removeNotificationToken: (token) => notificationService.removeNotificationToken(token)
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}; 